"use client";

import type React from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { findCustomCodeFormSlotTargets } from "./customCodeFormSlotRefs";

export interface CustomCodeFormSlot {
	content: React.ReactNode;
	ref: string;
}

const WRAPPER_STYLE = { display: "contents" } as const;

/**
 * Portals each published form into its `[data-cms-form]` placeholder inside the
 * custom code markup. Custom code HTML can be swapped after mount (static →
 * hydrated renderer, author JS), so targets are re-resolved on DOM changes.
 */
export function CustomCodeFormSlots({
	children,
	slots,
}: {
	children: React.ReactNode;
	slots: CustomCodeFormSlot[];
}) {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const [targets, setTargets] = useState<Record<string, HTMLElement>>({});
	const refsKey = slots.map((s) => s.ref).join("\n");

	useEffect(() => {
		const wrapper = wrapperRef.current;
		if (!wrapper) {
			return;
		}
		const refs = refsKey.split("\n").filter(Boolean);
		const resolve = () => {
			setTargets((prev) => {
				const next: Record<string, HTMLElement> = {};
				let changed = false;
				for (const ref of refs) {
					const [target] = findCustomCodeFormSlotTargets(wrapper, ref);
					if (target) {
						next[ref] = target;
					}
					if (next[ref] !== prev[ref]) {
						changed = true;
					}
				}
				return changed ? next : prev;
			});
		};
		resolve();
		const observer = new MutationObserver(resolve);
		observer.observe(wrapper, { childList: true, subtree: true });
		return () => observer.disconnect();
	}, [refsKey]);

	return (
		<div ref={wrapperRef} style={WRAPPER_STYLE}>
			{children}
			{slots.map((slot) => {
				const target = targets[slot.ref];
				return target ? (
					<PortalSlot content={slot.content} key={slot.ref} target={target} />
				) : null;
			})}
		</div>
	);
}

function PortalSlot({
	content,
	target,
}: {
	content: React.ReactNode;
	target: HTMLElement;
}) {
	return createPortal(content, target);
}
